import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsContext } from 'app/shared/utils';
import { useHotjar } from 'app/shared/utils';
import { calcEventState } from 'app/shared/utils/calcEventState';
import { useMerchandisingHeadlineAndDescription } from 'app/shared/utils/useMerchandisingHeadlineAndDescription';
import { useVisaAuth } from 'app/shared/utils/visa';
import { EventAttendee } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { neighborhoodOrSecretLocation } from 'app/fan/utils';
import { isEventInPast } from 'app/fan/utils/checkout';
import { getEventVenueTagForKlaviyo } from 'app/fan/utils/useGetEventTagsProps';
import { useEventPageContext } from 'app/fan/context/EventPage';

interface Props {
  eventAttendee?: EventAttendee;
}

export const useTrackProductViewed = ({
  eventAttendee: eventAttendeeProp,
}: Props = {}) => {
  const { formatMessage } = useIntl();
  const { event, eventAttendee: eventAttendeeFromContext } =
    useEventPageContext();
  const eventAttendee = eventAttendeeProp || eventAttendeeFromContext;
  const { user: loggedInUser } = useContext(AuthContext);
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const { visaPreSaleEligible } = useVisaAuth({
    skipLoadCardRedemptionsStatus: false,
  });
  const { sendHotjarStateChange } = useHotjar();

  const attendeeState = calcEventState({
    event,
    attendeeState: eventAttendee?.currentState,
  });
  sendHotjarStateChange(`event/upcoming/${attendeeState}`);

  const eventAttendeeTickets =
    eventAttendee && eventAttendee.visibleToUser && eventAttendee.tickets;

  const userHasTickets =
    eventAttendeeTickets && eventAttendeeTickets.length > 0;

  const twentyFourHoursFromNow = new Date(Date.now() + 60 * 60 * 1000 * 24);
  const eventIsInLessThan24Hours = event
    ? new Date(event.startsAt) < twentyFourHoursFromNow
    : false;

  const aboutThisConcert = useMerchandisingHeadlineAndDescription({ event });

  useEffect(() => {
    if (event) {
      const isInThePast = isEventInPast(event.startsAt);

      trackAnalyticsEvent(
        'Product Viewed',
        {
          product_id: event.id, // Snapchat needs this, please do not remove
          email: loggedInUser?.email?.toLowerCase().trim(), // Snapchat needs this, please do not remove
          city_slug: event.city.cachedSlug,
          category: event.type,
          price: event.price,
          is_visa_validated: visaPreSaleEligible,
          event_state: isInThePast ? 'past' : 'upcoming',
          user_state: attendeeState,
          content_type: 'product',
          content_ids: [event.id],
          exposed_test: {
            testimonial_carousel: false, // Different from Event page V1!
          },
          event_owner_type: event.eventOrganizedAs,
          page: 'event',
          currency: event.city?.currencyCode,
          user_has_tickets: !!userHasTickets,
          event_is_today: eventIsInLessThan24Hours,
          city_title: event.city.title,
          event_page_version: 2,
          about_this_concert: aboutThisConcert,
          rebrand: true,
          neighborhood: neighborhoodOrSecretLocation(event, formatMessage),
          local_starts_at: event.localStartsAt,
          venue_tag: getEventVenueTagForKlaviyo(event),
        },
        true,
        event.id
      );
    }
  }, [event, eventAttendee, visaPreSaleEligible]);
};
